import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { apiError } from "../../lib/format";
import { toast } from "sonner";
import { UserCircle, KeyReturn, Check } from "@phosphor-icons/react";
import PushSettings from "../../components/PushSettings";

export default function Profile() {
  const { user, refreshMe } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [pin, setPin] = useState({ current_pin: "", new_pin: "" });
  const [advisor, setAdvisor] = useState(null);

  useEffect(() => {
    if (user) setForm({ name: user.name, phone: user.phone, email: user.email });
    if (user?.assigned_advisor_id) {
      api.get(`/users/${user.assigned_advisor_id}`).then((r) => setAdvisor(r.data)).catch(() => {});
    }
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      await api.patch("/auth/profile", form);
      toast.success("Perfil actualizado");
      refreshMe();
    } catch (e) { toast.error(apiError(e)); }
  };

  const changePin = async (e) => {
    e.preventDefault();
    if (pin.new_pin.length !== 4) return toast.error("El nuevo PIN debe tener 4 dígitos");
    try {
      await api.post("/auth/change-pin", pin);
      toast.success("PIN actualizado");
      setPin({ current_pin: "", new_pin: "" });
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!user) return null;

  return (
    <div className="space-y-8 max-w-4xl">
      <header>
        <div className="label-mono mb-2">Perfil</div>
        <h1 className="font-display text-4xl font-black tracking-tighter">Tus datos</h1>
      </header>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="bento p-6 md:col-span-2">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-forest text-bg flex items-center justify-center font-display font-black text-2xl">
              {user.name?.[0]?.toUpperCase()}
            </div>
            <div>
              <div className="font-display text-2xl font-bold">{user.name}</div>
              <div className="label-mono">{user.role} · estado {user.status}</div>
            </div>
          </div>
          <dl className="grid sm:grid-cols-2 gap-4 text-sm">
            <Info k="RUT" v={user.rut} mono />
            <Info k="Rol" v={user.role} />
            <Info k="Correo" v={user.email} />
            <Info k="Teléfono" v={user.phone} />
          </dl>
        </div>

        <div className="bento p-6">
          <div className="label-mono mb-2">Asesor asignado</div>
          {advisor ? (
            <>
              <div className="font-display text-xl font-bold">{advisor.name}</div>
              <div className="text-sm text-inkMuted mt-1">{advisor.email}</div>
              <div className="font-mono text-xs text-inkMuted mt-2">{advisor.phone}</div>
            </>
          ) : (
            <div className="text-sm text-inkMuted">Aún no tienes asesor asignado.</div>
          )}
        </div>
      </div>

      <form onSubmit={saveProfile} className="bento p-6 space-y-4" data-testid="profile-form">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><UserCircle size={20} weight="duotone" /> Editar datos</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Nombre"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" data-testid="pf-name" /></Field>
          <Field label="Teléfono"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" data-testid="pf-phone" /></Field>
          <Field label="Correo"><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" data-testid="pf-email" /></Field>
        </div>
        <button className="h-11 px-5 bg-forest text-bg text-sm font-semibold flex items-center gap-2" data-testid="pf-save"><Check size={14} weight="bold" /> Guardar cambios</button>
      </form>

      <form onSubmit={changePin} className="bento p-6 space-y-4 max-w-xl" data-testid="pin-form">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><KeyReturn size={20} weight="duotone" /> Cambiar PIN</h2>
        <Field label="PIN actual">
          <input type="password" inputMode="numeric" maxLength={4} value={pin.current_pin} onChange={(e) => setPin({ ...pin, current_pin: e.target.value.replace(/\D/g, "") })} className="input font-mono text-xl tracking-[0.5em]" data-testid="pin-current" />
        </Field>
        <Field label="Nuevo PIN">
          <input type="password" inputMode="numeric" maxLength={4} value={pin.new_pin} onChange={(e) => setPin({ ...pin, new_pin: e.target.value.replace(/\D/g, "") })} className="input font-mono text-xl tracking-[0.5em]" data-testid="pin-new" />
        </Field>
        <button className="h-11 px-5 bg-burnt text-bg text-sm font-semibold" data-testid="pin-save">Actualizar PIN</button>
      </form>

      <PushSettings />

      <style>{`
        .input { width: 100%; height: 48px; padding: 0 14px; border: 1px solid rgba(13,31,22,0.2); background: #fff; font-size: 15px; }
        .input:focus { outline: none; border-color: #0A2540; box-shadow: inset 0 0 0 1px #0A2540; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (<div><label className="label-mono block mb-1.5">{label}</label>{children}</div>);
}
function Info({ k, v, mono }) {
  return (
    <div className="py-2">
      <div className="label-mono">{k}</div>
      <div className={`${mono ? "font-mono" : ""} text-sm mt-0.5`}>{v}</div>
    </div>
  );
}
